import React from "react";

const escapeHtmlChars = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

// Regex to match complete mention pattern @[Name](id)
export const mentionRegex = /((.)\[([^\[\]]*)\]\(([^()]*)\))/g;

// Regex to match partial/incomplete mention pattern @[Name] or @[Name](partial...
export const partialMentionRegex = /@\[([^\[\]]*)\](\([^)]*)?$/;

// Known translations of "All" in different languages (lowercase for comparison)
export const ALL_TRANSLATIONS = [
  "all", "alle", "tout", "tous", "tutti", "todos", "tudo", "wszystko", "všichni",
  "alla", "kaikki", "összes", "vše", "toți", "всички", "все", "всі",
  "すべて", "全部", "모두", "semua", "tất cả", "όλοι", "الكل", "הכל", "सभी"
];

export const isAllMentionName = (name, id) => {
  if (!name && !id) return false;

  if (id) {
    const trimmedId = String(id).trim().toLowerCase();
    if (trimmedId === "idall12322" || trimmedId.includes("idall")) {
      return true;
    }
  }

  if (name) {
    const trimmedName = String(name).trim().toLowerCase();
    if (ALL_TRANSLATIONS.includes(trimmedName)) {
      return true;
    }
  }

  return false;
};

// Helper to render text with styled mentions
export const renderTextWithMentions = (text, strings = null) => {
  if (!text) return null;

  const parts = [];
  let lastIndex = 0;
  let match;
  let processedText = text;

  const partialMatch = text.match(partialMentionRegex);
  if (partialMatch) {
    processedText = text.substring(0, partialMatch.index);
  }

  mentionRegex.lastIndex = 0;

  while ((match = mentionRegex.exec(processedText)) !== null) {
    if (match.index > lastIndex) {
      parts.push(processedText.substring(lastIndex, match.index));
    }

    const trigger = match[2];
    const name = match[3];
    const id = match[4];
    const displayName = isAllMentionName(name, id) && strings?.All ? strings.All : name;

    parts.push(
      <span key={match.index} className="mention-highlight" style={{ fontWeight: 600 }}>
        {trigger}{displayName}
      </span>
    );

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < processedText.length) {
    parts.push(processedText.substring(lastIndex));
  }

  if (partialMatch) {
    const partialName = partialMatch[1];
    const displayPartialName = isAllMentionName(partialName, null) && strings?.All ? strings.All : partialName;
    parts.push(
      <span key="partial-mention" className="mention-highlight" style={{ fontWeight: 600 }}>
        @{displayPartialName}
      </span>
    );
  }

  return parts.length > 0 ? parts : text;
};

export const preprocessMentionsForMarkdown = (text, strings) => {
  if (!text) return '';
  let processed = text;
  processed = processed.replace(/((.)\[([^\[\]]*)\]\(([^()]*)\))/g, (match, full, trigger, name, id) => {
    const displayName = isAllMentionName(name, id) && strings?.All ? strings.All : name;
    return `<span class="mention-highlight" style="font-weight:600">${escapeHtmlChars(trigger)}${escapeHtmlChars(displayName)}</span>`;
  });
  const partialMatch = processed.match(/@\[([^\[\]]*)\](\([^)]*)?$/);
  if (partialMatch) {
    const partialName = partialMatch[1];
    const displayName = isAllMentionName(partialName, null) && strings?.All ? strings.All : partialName;
    processed = processed.replace(/@\[([^\[\]]*)\](\([^)]*)?$/, `<span class="mention-highlight" style="font-weight:600">@${escapeHtmlChars(displayName)}</span>`);
  }
  processed = processed.replace(
    /(^|[\s(])@([^\s@,!?;:()[\]{}<>]+(?:\s+[^\s@,!?;:()[\]{}<>]+)+)/g,
    (match, prefix, fullName) => `${prefix}<span class="mention-highlight" style="font-weight:600">@${escapeHtmlChars(fullName)}</span>`
  );
  processed = processed.replace(
    /(^|[\s(])@([^\s@,!?;:()[\]{}<>]+)/g,
    (match, prefix, username) => `${prefix}<span class="mention-highlight" style="font-weight:600">@${escapeHtmlChars(username)}</span>`
  );
  return processed;
};

export const closeTruncatedMarkdown = (text) => {
  if (!text) return text;
  const markers = ['**', '~~'];
  let result = text;
  for (const marker of markers) {
    let count = 0;
    let idx = 0;
    while (idx < result.length) {
      const pos = result.indexOf(marker, idx);
      if (pos === -1) break;
      count++;
      idx = pos + marker.length;
    }
    if (count % 2 !== 0) {
      result += marker;
    }
  }
  const openU = (result.match(/<u>/gi) || []).length;
  const closeU = (result.match(/<\/u>/gi) || []).length;
  if (openU > closeU) {
    result += '</u>';
  }
  const openEm = (result.match(/<em>/gi) || []).length;
  const closeEm = (result.match(/<\/em>/gi) || []).length;
  if (openEm > closeEm) {
    result += '</em>';
  }
  return result;
};
